import React, { useState, useRef, Fragment, createElement } from 'react';
import { Snapshot, Holding, Settings, LiquidityEvent, Loan, Currency } from '../types';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, Label } from '../components/ui/Forms';
import { Camera, Trash2, Calendar, Database, Download, Upload, AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
interface CurrentData {
  holdings: Holding[];
  settings: Settings;
  partnerNames: [string, string];
  liquidityEvents: LiquidityEvent[];
  loans: Loan[];
  exchangeRates: Record<Currency, number>;
}
interface SnapshotsPageProps {
  snapshots: Snapshot[];
  onCreateSnapshot: (name: string) => void;
  onDeleteSnapshot: (id: string) => void;
  onImportSnapshot: (snapshot: Snapshot) => void;
  onRestoreSnapshot: (snapshot: Snapshot) => void;
  currentData: CurrentData;
}
function downloadSnapshot(snapshot: Snapshot) {
  const json = JSON.stringify(snapshot, null, 2);
  const blob = new Blob([json], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const datePart = new Date(snapshot.createdAt).toISOString().slice(0, 10);
  const safeName = snapshot.name.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  a.download = `pairfolio-${safeName}-${datePart}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
export function SnapshotsPage({
  snapshots,
  onCreateSnapshot,
  onDeleteSnapshot,
  onImportSnapshot,
  onRestoreSnapshot,
  currentData
}: SnapshotsPageProps) {
  const [newSnapshotName, setNewSnapshotName] = useState('');
  const [importStatus, setImportStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null);
  const [restoreStatus, setRestoreStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputLiveRef = useRef<HTMLInputElement>(null);
  const [importMode, setImportMode] = useState<'snapshot' | 'live'>('snapshot');
  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newSnapshotName.trim() || `Snapshot ${new Date().toLocaleDateString()}`;
    onCreateSnapshot(name);
    setNewSnapshotName('');
  };
  const handleExportCurrent = () => {
    const exportData: Snapshot = {
      id: Math.random().toString(36).substr(2, 9),
      name: `Export ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      holdings: JSON.parse(JSON.stringify(currentData.holdings)),
      settings: JSON.parse(JSON.stringify(currentData.settings)),
      partnerNames: [...currentData.partnerNames],
      liquidityEvents: JSON.parse(JSON.stringify(currentData.liquidityEvents)),
      loans: JSON.parse(JSON.stringify(currentData.loans)),
      exchangeRates: {
        ...currentData.exchangeRates
      }
    };
    downloadSnapshot(exportData);
  };
  const processImportFile = (file: File, restoreAsLive: boolean) => {
    setImportStatus(null);
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (!data.holdings || !Array.isArray(data.holdings)) {
          throw new Error('Invalid file: missing holdings array');
        }
        if (!data.settings || typeof data.settings !== 'object') {
          throw new Error('Invalid file: missing settings object');
        }
        if (!data.partnerNames || !Array.isArray(data.partnerNames)) {
          throw new Error('Invalid file: missing partnerNames');
        }
        const snapshot: Snapshot = {
          id: Math.random().toString(36).substr(2, 9),
          name: data.name || `Imported ${new Date(data.createdAt || Date.now()).toLocaleDateString()}`,
          createdAt: data.createdAt || new Date().toISOString(),
          holdings: data.holdings,
          settings: data.settings,
          partnerNames: data.partnerNames,
          liquidityEvents: data.liquidityEvents || [],
          loans: data.loans || [],
          exchangeRates: data.exchangeRates || undefined
        };
        if (restoreAsLive) {
          onRestoreSnapshot(snapshot);
          setImportStatus({
            type: 'success',
            message: `Restored "${snapshot.name}" as live data — ${snapshot.holdings.length} holdings, ${(snapshot.liquidityEvents || []).length} events, ${(snapshot.loans || []).length} loans loaded.`
          });
        } else {
          onImportSnapshot(snapshot);
          setImportStatus({
            type: 'success',
            message: `Imported "${snapshot.name}" as snapshot (${snapshot.holdings.length} holdings, from ${new Date(snapshot.createdAt).toLocaleDateString()})`
          });
        }
      } catch (err: any) {
        setImportStatus({
          type: 'error',
          message: err.message || 'Failed to parse the file. Please ensure it is a valid Pairfolio export.'
        });
      }
    };
    reader.readAsText(file);
  };
  const handleImportAsSnapshot = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImportFile(file, false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleImportAsLive = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImportFile(file, true);
    if (fileInputLiveRef.current) fileInputLiveRef.current.value = '';
  };
  const handleRestore = (snapshot: Snapshot) => {
    onRestoreSnapshot(snapshot);
    setConfirmRestoreId(null);
    setRestoreStatus(`Restored "${snapshot.name}" as live data.`);
    setTimeout(() => setRestoreStatus(null), 4000);
  };
  return <div className="space-y-6" data-id="element-1900">
      <Card data-id="element-1901">
        <CardHeader title="Create Snapshot" subtitle="Save a point-in-time copy of all your holdings, settings, and liquidity events." data-id="element-1902" />
        <CardContent data-id="element-1903">
          <form onSubmit={handleCreate} className="flex items-end gap-4" data-id="element-1904">
            <div className="flex-1" data-id="element-1905">
              <Label data-id="element-1906">Snapshot Name</Label>
              <Input placeholder={`e.g. End of Q3 ${new Date().getFullYear()}`} value={newSnapshotName} onChange={e => setNewSnapshotName(e.target.value)} data-id="element-1907" />
            </div>
            <Button type="submit" className="shrink-0" data-id="element-1908">
              <Camera className="w-4 h-4 mr-2" data-id="element-1909" />
              Save Snapshot
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Export / Import */}
      <Card data-id="element-1910">
        <CardHeader title="Export & Import" subtitle="Transfer your portfolio data between devices or create backups." data-id="element-1911" />
        <CardContent data-id="element-1912">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1913">
            {/* Export current */}
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col" data-id="element-1914">
              <div className="flex items-center gap-2 mb-2" data-id="element-1915">
                <Download className="w-4 h-4 text-blue-600" data-id="element-1916" />
                <h4 className="text-sm font-semibold text-slate-800" data-id="element-1917">
                  Export Live Data
                </h4>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1" data-id="element-1918">
                Download your current live state as a JSON file.
              </p>
              <Button variant="secondary" onClick={handleExportCurrent} className="w-full mt-auto" data-id="element-1919">
                <Download className="w-4 h-4 mr-2" data-id="element-1920" />
                Export
              </Button>
            </div>

            {/* Import as snapshot */}
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col" data-id="element-1921">
              <div className="flex items-center gap-2 mb-2" data-id="element-1922">
                <Upload className="w-4 h-4 text-emerald-600" data-id="element-1923" />
                <h4 className="text-sm font-semibold text-slate-800" data-id="element-1924">
                  Import as Snapshot
                </h4>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1" data-id="element-1925">
                Add an exported file as a saved snapshot for comparison.
              </p>
              <input ref={fileInputRef} type="file" accept=".json,application/json" onChange={handleImportAsSnapshot} className="hidden" data-id="element-1926" />
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()} className="w-full mt-auto" data-id="element-1927">
                <Upload className="w-4 h-4 mr-2" data-id="element-1928" />
                Import
              </Button>
            </div>

            {/* Import as live */}
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50 flex flex-col" data-id="element-1929">
              <div className="flex items-center gap-2 mb-2" data-id="element-1930">
                <RotateCcw className="w-4 h-4 text-amber-600" data-id="element-1931" />
                <h4 className="text-sm font-semibold text-slate-800" data-id="element-1932">
                  Restore from File
                </h4>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1" data-id="element-1933">
                Replace all live data with an exported file. This overwrites
                everything.
              </p>
              <input ref={fileInputLiveRef} type="file" accept=".json,application/json" onChange={handleImportAsLive} className="hidden" data-id="element-1934" />
              <Button variant="secondary" onClick={() => fileInputLiveRef.current?.click()} className="w-full mt-auto text-amber-700 border-amber-300 hover:bg-amber-100" data-id="element-1935">
                <RotateCcw className="w-4 h-4 mr-2" data-id="element-1936" />
                Restore
              </Button>
            </div>
          </div>

          {importStatus && <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg text-sm ${importStatus.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-1937">
              {importStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1938" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" data-id="element-1939" />}
              {importStatus.message}
            </div>}
        </CardContent>
      </Card>

      {/* Restore status banner */}
      {restoreStatus && <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-emerald-50 text-emerald-800 border border-emerald-200" data-id="element-1940">
          <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-1941" />
          {restoreStatus}
        </div>}

      <Card data-id="element-1942">
        <CardHeader title="Saved Snapshots" subtitle="View, export, restore, or delete your historical snapshots." data-id="element-1943" />
        <CardContent className="p-0" data-id="element-1944">
          {snapshots.length === 0 ? <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center" data-id="element-1945">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-1946">
                <Camera className="w-6 h-6 text-slate-400" data-id="element-1947" />
              </div>
              <p data-id="element-1948">No snapshots created yet.</p>
              <p className="text-sm mt-1" data-id="element-1949">
                Create your first snapshot above to save your current progress.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1950">
              <table className="w-full text-sm text-left" data-id="element-1951">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1952">
                  <tr data-id="element-1953">
                    <th className="px-6 py-3 font-medium" data-id="element-1954">Name</th>
                    <th className="px-6 py-3 font-medium" data-id="element-1955">Date Created</th>
                    <th className="px-6 py-3 font-medium" data-id="element-1956">Data Summary</th>
                    <th className="px-6 py-3 font-medium text-right" data-id="element-1957">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-1958">
                  {snapshots.map(snapshot => <Fragment key={snapshot.id} data-id="element-1959">
                      <tr className="hover:bg-slate-50/50 transition-colors group" data-id="element-1960">
                        <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1961">
                          {snapshot.name}
                        </td>
                        <td className="px-6 py-4 text-slate-500" data-id="element-1962">
                          <div className="flex items-center gap-1.5" data-id="element-1963">
                            <Calendar className="w-4 h-4 text-slate-400" data-id="element-1964" />
                            {new Date(snapshot.createdAt).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-500" data-id="element-1965">
                          <div className="flex items-center gap-1.5" data-id="element-1966">
                            <Database className="w-4 h-4 text-slate-400" data-id="element-1967" />
                            {snapshot.holdings.length} holdings
                            {snapshot.holdings.filter(h => h.isRealEstate).length > 0 && <span className="text-indigo-600" data-id="element-1968">
                                (
                                {snapshot.holdings.filter(h => h.isRealEstate).length}{' '}
                                properties)
                              </span>}
                            , {snapshot.liquidityEvents.length} events,{' '}
                            {(snapshot.loans || []).length} loans
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-1969">
                          <div className="flex items-center justify-end gap-1" data-id="element-1970">
                            <Button variant="ghost" size="sm" onClick={() => downloadSnapshot(snapshot)} className="text-slate-400 hover:text-blue-600 px-2" title="Export Snapshot" data-id="element-1971">
                              <Download className="w-4 h-4" data-id="element-1972" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmRestoreId(confirmRestoreId === snapshot.id ? null : snapshot.id)} className={`px-2 ${confirmRestoreId === snapshot.id ? 'text-amber-600 bg-amber-50' : 'text-slate-400 hover:text-amber-600'}`} title="Restore as Live Data" data-id="element-1973">
                              <RotateCcw className="w-4 h-4" data-id="element-1974" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => onDeleteSnapshot(snapshot.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete Snapshot" data-id="element-1975">
                              <Trash2 className="w-4 h-4" data-id="element-1976" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {confirmRestoreId === snapshot.id && <tr className="bg-amber-50 border-b border-amber-200" data-id="element-1977">
                          <td colSpan={4} className="px-6 py-3" data-id="element-1978">
                            <div className="flex items-center justify-between gap-4" data-id="element-1979">
                              <p className="text-sm text-amber-800" data-id="element-1980">
                                <strong data-id="element-1981">Restore "{snapshot.name}"?</strong> This
                                will overwrite all your current live holdings,
                                settings, events, and loans.
                              </p>
                              <div className="flex items-center gap-2 shrink-0" data-id="element-1982">
                                <Button variant="secondary" size="sm" onClick={() => setConfirmRestoreId(null)} data-id="element-1983">
                                  Cancel
                                </Button>
                                <Button size="sm" onClick={() => handleRestore(snapshot)} className="bg-amber-600 hover:bg-amber-700 text-white" data-id="element-1984">
                                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" data-id="element-1985" />
                                  Confirm Restore
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table>
            </div>}
        </CardContent>
      </Card>
    </div>;
}